import { SaleItemState, SaleReportHisItem } from "@/types/sales";
import {
  Button,
  Empty,
  Flex,
  Input,
  Popover,
  TableProps,
  Tooltip,
  Typography,
  App,
} from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import useDataList from "@/hooks/useDataList";
import api from "@/apis/order";
import { CommonListParams } from "@/types/common";
import SText from "@/styles/SText";
import { MenuOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { dateFormat } from "@/utils/format";
import OrderForm from "@/pages/Sales/components/OrderForm";
import { numFormmater } from "../Requirement/util";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

const { Text } = Typography;

interface DataReportFilters {
  keyword: string;
}

const INITIAL_DATA_REPORT_FILTERS: DataReportFilters = {
  keyword: '',
};

const DataReport: FC = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList }] = useDataList<SaleItemState, [CommonListParams<string>]>({ fetchApi: api.itemStateList });

  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [detail, setDetail] = useState<SaleItemState | null>(null);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<DataReportFilters>(INITIAL_DATA_REPORT_FILTERS);
  const { keyword } = draftFilters;

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_DATA_REPORT_FILTERS);
    fetch(0, nextFilters);
    // 首次加载只执行一次，筛选提交由事件显式触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceFilters]);

  const handleExportAll = async (): Promise<SaleItemState[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allData: SaleItemState[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await api.itemStateList({
          body: appliedFilters.keyword,
          pagination: { offset, take: batchSize },
        });

        if (response.body && response.body.length > 0) {
          allData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allData.length /
                (response.pagination?.size || allData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);

          if (response.body.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setExportProgress(100);
      return allData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const reportColumns = [
    { key: 'orderNumber', label: t('sale.orderNumber') },
    { key: 'innerNumber', label: t('sale.innerNumber') },
    { key: 'customerCode', label: t('sale.code') },
    { key: 'customerShortName', label: t('sale.shortName') },
    { key: 'productCode', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'model', label: t('product.brand1') },
    { key: 'quantity', label: t('sale.saleTotalQuantity') },
    { key: 't1', label: 'SMT' },
    { key: 't2', label: 'DIP' },
    { key: 't3', label: 'TEST1' },
    { key: 't4', label: 'ASSY' },
    { key: 't5', label: 'TEST2' },
    { key: 't6', label: 'PACK' },
    { key: 't7', label: t('sale.inStock') },
    { key: 't8', label: t('sale.outPro') },
  ];

  const reportDataMapper = (item: SaleItemState, t: any) => {
    return {
      [t('sale.orderNumber')]: item.order?.number || '',
      [t('sale.innerNumber')]: item.order?.innerNumber || '',
      [t('sale.code')]: item.customer?.code || '',
      [t('sale.shortName')]: item.customer?.shortName || '',
      [t('product.code')]: item.product?.code || '',
      [t('product.name')]: item.product?.name || '',
      [t('product.brand1')]: item.product?.model || '',
      [t('sale.saleTotalQuantity')]: item.quantity || 0,
      'SMT': item.prodState?.t1 || 0,
      'DIP': item.prodState?.t2 || 0,
      'TEST1': item.prodState?.t3 || 0,
      'ASSY': item.prodState?.t4 || 0,
      'TEST2': item.prodState?.t5 || 0,
      'PACK': item.prodState?.t6 || 0,
      [t('sale.inStock')]: item.prodState?.t7 || 0,
      [t('sale.outPro')]: item.prodState?.t8 || 0,
    };
  };

  const getProgressEle = (
    item: SaleItemState,
    key: Extract<keyof SaleReportHisItem, `t${number}`>
  ) => {
    return (
      <Flex style={{ width: "100%" }}>
        <SText
          key={item.id + key}
          text={numFormmater(Number(item.prodState?.[key] ?? 0))}
        />
        <Popover
          showArrow={false}
          trigger={["click"]}
          content={() => (
            <div
              style={{
                width: 200,
                height: "auto",
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {item.prodStateHis && item.prodStateHis?.length > 0 ? (
                item.prodStateHis.map((item) => (
                  <Flex style={{ width: "100%" }} justify="space-between">
                    <Text type="secondary">
                      {dateFormat(dayjs(parseInt(item.date.toString())))}
                    </Text>
                    <Text code>{item[key]}</Text>
                  </Flex>
                ))
              ) : (
                <Empty description={t("sale.notReportData")} />
              )}
            </div>
          )}
        >
          <Button icon={<MenuOutlined />} type="link"></Button>
        </Popover>
      </Flex>
    );
  };

  const columns: TableProps<SaleItemState>["columns"] = [
    {
      title: t("sale.orderInfo"),
      children: [
        {
          title: <SText text={t("sale.orderNumber")} />,
          key: "orderNumber",
          dataIndex: ["order", "number"],
          width: 200,
          fixed: "left",
          sorter: (a, b) => {
            const valA = a?.order?.number ?? "";
            const valB = b?.order?.number ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <Typography.Text
              key={item.id + "number"}
              ellipsis={{ tooltip: item.order?.number ?? "" }}
              style={{ color: '#1890ff', cursor: 'pointer', width: '100%' }}
              onClick={() => setDetail(item)}
            >
              {item.order?.number ?? ""}
            </Typography.Text>
          ),
        },
        {
          title: <SText text={t("sale.innerNumber")} />,
          key: "orderInnerNumber",
          dataIndex: ["order", "innerNumber"],
          width: 120,
          fixed: "left",
          sorter: (a, b) => {
            const valA = a?.order?.innerNumber ?? "";
            const valB = b?.order?.innerNumber ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <SText
              key={item.id + "innerNumber"}
              text={item.order?.innerNumber ?? ""}
            />
          ),
        },
      ],
    },
    {
      title: t("sale.customerInfo"),
      children: [
        {
          title: <SText text={t("sale.code")} />,
          key: "customerCode",
          dataIndex: ["customer", "code"],
          width: 120,
          sorter: (a, b) => {
            const valA = a?.customer?.code ?? "";
            const valB = b?.customer?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <SText
              key={item.id + "customerCode"}
              text={item.customer?.code ?? ""}
            />
          ),
        },
        {
          title: <SText text={t("sale.shortName")} />,
          key: "customerShortName",
          width: 220,
          render: (_, item) => (
            <SText
              key={item.id + "customerShortName"}
              text={item.customer?.shortName ?? ""}
            />
          ),
        },
      ],
    },
    {
      title: t("sale.proInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          key: "productCode",
          dataIndex: ["product", "code"],
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.code ?? "";
            const valB = b?.product?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.code ?? ""}
            />
          ),
        },
        {
          title: <SText text={t("product.name")} />,
          key: "productName",
          width: 180,
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.name ?? ""}
            />
          ),
        },
        {
          title: <SText text={t("product.brand1")} />,
          key: "model",
          width: 180,
          render: (_, item) => (
            <SText
              key={item.id + "model"}
              text={item.product?.model ?? ""}
            />
          ),
        },
      ],
    },
    {
      title: <SText text={t("sale.reportInfo")} />,
      children: [
        {
          title: <SText text={t("sale.saleTotalQuantity")} />,
          key: "quantity",
          width: 120,
          render: (_, item) => (
            <SText
              key={item.id + "quantity"}
              text={numFormmater(Number(item.quantity ?? 0))}
            />
          ),
        },
        {
          title: <SText text="SMT" />,
          key: "reportT1",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t1"),
        },
        {
          title: <SText text="DIP" />,
          key: "reportT2",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t2"),
        },
        {
          title: <SText text="TEST1" />,
          key: "reportT3",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t3"),
        },
        {
          title: <SText text="ASSY" />,
          key: "reportT4",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t4"),
        },
        {
          title: <SText text="TEST2" />,
          key: "reportT5",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t5"),
        },
        {
          title: <SText text="PACK" />,
          key: "reportT6",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t6"),
        },
        {
          title: <SText text={t("sale.inStock")} />,
          key: "reportT7",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t7"),
        },
        {
          title: <SText text={t("sale.outPro")} />,
          key: "reportT8",
          width: 60,
          render: (_, item) => getProgressEle(item ?? [], "t8"),
        },
      ],
    },
  ];

  const fetch = (offset: number, filters: DataReportFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchList({
      body: filters.keyword,
      pagination: { offset, take },
    }).catch((err) => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_DATA_REPORT_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const headerChildren = (
    <Flex style={{ width: "100%", marginBottom: 20 }} justify="space-between" align="center">
      <Flex gap={10}>
        <ExportExcelButton
          list={list}
          columns={reportColumns}
          defaultSelectedColumns={[t('sale.orderNumber'), t('sale.innerNumber'), t('product.code')]}
          dataMapper={reportDataMapper}
          fileName={`${t('layout.orderReport')}.xlsx`}
        />
        <ExportExcelButton
          isAll
          loadData={handleExportAll}
          columns={reportColumns}
          defaultSelectedColumns={[t('sale.orderNumber'), t('sale.innerNumber'), t('product.code')]}
          dataMapper={reportDataMapper}
          fileName={`${t('layout.orderReportAll')}.xlsx`}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("sale.reportSearchTip")}>
              <Input
                placeholder={t("sale.reportSearchTip")}
                style={{ width: 200 }}
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  )

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={1400}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => fetch(offset, appliedFilters)}
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      {detail && <OrderForm
        key={detail?.id}
        data={detail}
        open={!!detail}
        handleCancel={() => setDetail(null)}
        leftTitle={t("common.detail")}
        disabledItems={[
          'innerNumber',
          'receiver',
          'receiverContact',
          'receiverMobile',
          'receiverAddress',
          'country',
          'extPrice',
          'taxRate',
          'shippingMethod',
          'itemPriceHis',
          'quantity',
          'bquantity',
          'discountedPrice',
          'discount',
          'bdiscountedPrice',
          'bdiscount',
          'mfgr',
          'customerModel',
          'reqDeliDate',
          'currency',
          'commissionRate',
          'preFinishDate',
          'smtStartDate',
          'preFinishDate'
        ]}
        confirmLoading={false}
        handleOk={() => Promise.resolve()}
      />}
    </Flex>
  );
};

export default DataReport;
